import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/about/
───────────────────────────────────────────── */
import MicImg       from "../../assets/images/about/about-us-mic.webp";
import LogoQuench      from "../../assets/images/about/logo-quench.webp";
import LogoAdorDig     from "../../assets/images/about/logo-ador-digatron.png";
import LogoYonder      from "../../assets/images/about/logo-yonder.png";
import LogoAdor        from "../../assets/images/about/logo-ador.png";
import TestimonialBg   from "../../assets/images/about/testimonial-bg.webp";

/* ─────────────────────────────────────────────
   ICON IMPORTS — src/assets/icons/about/
───────────────────────────────────────────── */
import IconYoutube      from "../../assets/icons/about/youtube.svg";
import IconSpotify      from "../../assets/icons/about/spotify.svg";
import IconApplePodcast from "../../assets/icons/about/apple-podcast.svg";
import IconQuoteImg     from "../../assets/icons/about/quote.svg";

/* ─────────────────────────────────────────────
   AboutPage.jsx
   Prefix  : ab-
   Font    : Poppins
   Purple  : #7B2FBE
   BG      : #1A1A1A  |  Know Us: #111111
   Max-W   : 1600px   |  Pad: 102px
   Breaks  : 768px    |  480px
   Animations:
     - Stat cards: slide from LEFT on page load (staggered)
     - Brand cards: 3D flip on hover
     - All sections: fade-up on scroll
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .ab-wrap *, .ab-wrap *::before, .ab-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .ab-wrap {
    font-family: 'Montserrat', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .ab-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }

  /* ── SCROLL FADE-UP ── */
  .ab-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .ab-fade-up.ab-in {
    opacity: 1;
    transform: translateY(0);
  }
  .ab-delay-1 { transition-delay: 0.08s; }
  .ab-delay-2 { transition-delay: 0.18s; }
  .ab-delay-3 { transition-delay: 0.28s; }
  .ab-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     SECTION 1 — ABOUT US
     3-col: [stat cards] | [mic image] | [text]
  ══════════════════════════════════════ */
  .ab-about {
    width: 100%;
    background: #000000;
    padding: 60px 0;
  }
  .ab-about__row {
    display: flex;
    align-items: flex-start;
    gap: 80px;
  }

  /* ── STAT CARDS COL (left) ── */
  .ab-stats-col {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 30px;
    width: 136px;
  }

  /* Slide-in from left — initial state */
  .ab-stat-card {
    background: linear-gradient(180deg, #000000 0%, #743FAC 100%);
    border-radius: 10px;
    padding: 12.5px 15px;
    text-align: center;
    opacity: 0;
    transform: translateX(-80px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
  /* Triggered state */
  .ab-stat-card.ab-slide-in {
    opacity: 1;
    transform: translateX(0);
  }
  .ab-stat-card--d1 { transition-delay: 0.0s; }
  .ab-stat-card--d2 { transition-delay: 0.12s; }
  .ab-stat-card--d3 { transition-delay: 0.24s; }
  .ab-stat-card--d4 { transition-delay: 0.36s; }

  .ab-stat-card__num {
    display: block;
    font-size: 38px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
  }
  .ab-stat-card__label {
    display: block;
    font-size: 16px;
    font-weight: 400;
    color: #FFFFFF;
    margin-top: 5px;
    letter-spacing: 0.2px;
  }

  /* ── MIC IMAGE COL (center) ── */
  .ab-mic-col {
    flex-shrink: 0;
    width: 350px;
  }
  .ab-mic-img {
    width: 100%;
    height: 481px;
    object-fit: cover;
    border-radius: 10px;
    display: block;
  }

  /* ── TEXT COL (right) ── */
  .ab-text-col {
    flex: 1;
    min-width: 0;
  }
  .ab-text-col__heading {
    font-size: 24px;
    font-weight: 700;
    color: #743FAC;
    margin-bottom: 14px;
  }
  .ab-text-col p {
    font-size: 16px;
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    line-height: 140%;
    color: #FFFFFF;
    margin-bottom: 14px;
  }
  .ab-text-col p:last-child { margin-bottom: 0; }

  /* ══════════════════════════════════════
     SECTION 2 — KNOW US BETTER
  ══════════════════════════════════════ */
  .ab-know {
    width: 100%;
    background: #111111;
    padding: 100px 0 100px;
  }
  .ab-know__heading {
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    text-align: center;
    margin-bottom: 60px;
  }
  .ab-know__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }

  /* ── 3D FLIP CARD ── */
  .ab-flip-wrap {
    perspective: 1000px;
    height: 260px;
    cursor: pointer;
  }
  .ab-flip-inner {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transition: transform 0.55s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .ab-flip-wrap:hover .ab-flip-inner {
    transform: rotateX(180deg);
  }

  /* Front face */
  .ab-flip-front,
  .ab-flip-back {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 44px;
  }

  .ab-flip-front {
    background: #ffffff;
    border: 1px solid #e5e5e5;
  }
  .ab-flip-front__logo {
    height: 30px !important;
    width: 198px !important;
    object-fit: contain;
    margin-bottom: 24px;
    display: block;
  }
  .ab-flip-front__text {
    font-size: 16px;
    font-weight: 400;
    line-height: 140%;
    color: #1E1E1E;
    text-align: center;
    font-family: 'Roboto', sans-serif;
  }

  /* Back face */
  .ab-flip-back {
    background: #743FAC;
    transform: rotateX(180deg);
    gap: 24px;
  }
  .ab-flip-back__text {
    font-size: 16px;
    font-weight: 400;
    line-height: 140%;
    color: #FFFFFF;
    text-align: center;
    font-family: 'Roboto', sans-serif;
  }
  .ab-flip-back__link {
    display: inline-block;
    margin-top: 6px;
    font-size: 16px;
    font-weight: 500;
    color: #743FAC;
    background: #ffffff;
    border-radius: 50px;
    padding: 10px 20px;
    text-decoration: none;
    letter-spacing: 0.2px;
    transition: background 0.2s;
    font-family: 'Roboto', sans-serif;
    text-decoration: underline;
  }
  .ab-flip-back__link:hover {
    background: rgba(255,255,255,0.85);
  }

  /* Ador card back — dark instead of purple */
  .ab-flip-wrap--ador .ab-flip-back {
    background: #743FAC;
  }
  .ab-flip-wrap--ador .ab-flip-back__logo {
    filter: none;
    opacity: 1;
  }

  /* ══════════════════════════════════════
     SECTION 3 — TESTIMONIALS
  ══════════════════════════════════════ */
  .ab-testi {
    width: 100%;
    position: relative;
    overflow: hidden;
    padding: 60px 0 60px;
  }
  .ab-testi__bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    // filter: brightness(0.50) saturate(0.9);
    z-index: 0;
  }
  .ab-testi__overlay {
    position: absolute;
    inset: 0;
    // background: linear-gradient(180deg, rgba(20,5,45,0.78) 0%, rgba(8,3,20,0.88) 100%);
    z-index: 1;
  }
  .ab-testi__content {
    position: relative;
    z-index: 2;
  }
  .ab-testi__label {
    font-size: 24px;
    font-weight: 700;
    color: #743FAC;
    text-align: center;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 14px;
  }
  .ab-testi__heading {
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    text-align: center;
    margin-bottom: 60px;
  }
  .ab-testi__cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    margin-bottom: 36px;
  }
  .ab-testi-card {
    border: 1px solid #743FAC;
    border-radius: 10px;
    padding: 44px;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 180px;
    transition: border-color 0.25s;
  }
  .ab-testi-card:hover { border-color: rgba(155,89,245,0.75); }
  .ab-testi-card__quote {
    font-size: 18px;
    line-height: 160%;
    color: #FFFFFF;
    margin-bottom: 24px;
    flex: 1;
  }
  .ab-testi-card__footer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .ab-testi-card__author {
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
  }
  .ab-testi-card__quote-icon {
    color: #7B2FBE;
    flex-shrink: 0;
  }
  /* Dots */
  .ab-testi__dots {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }
  .ab-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #8B8B8B;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: background 0.2s;
  }
  .ab-dot.ab-dot--on {
    background: #743FAC;
    width: 8px;
    border-radius: 4px;
  }

  /* ── CAROUSEL TRACK ── */
  .ab-testi__track-wrap {
    overflow: hidden;
    width: 100%;
    cursor: grab;
    user-select: none;
    margin-bottom: 36px;
  }
  .ab-testi__track-wrap.ab-dragging {
    cursor: grabbing;
  }
  .ab-testi__track {
    display: flex;
    transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
  }
  .ab-testi__track.ab-no-transition {
    transition: none;
  }
  .ab-testi-slide {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    flex: 0 0 100%;
    width: 100%;
  }

  /* ══════════════════════════════════════
     SECTION 4 — LISTEN & SUBSCRIBE
  ══════════════════════════════════════ */
  .ab-listen {
    width: 100%;
    background: #000000;
    padding: 40px 0;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .ab-listen__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
  .ab-listen__label {
    font-size: 24px;
    font-weight: 600;
    color: #FFFFFF;
    letter-spacing: 0.2px;
  }
  .ab-listen__platforms {
    display: flex;
    align-items: center;
    gap: 184px;
  }
  .ab-platform-link {
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    transition: opacity 0.2s;
  }
  .ab-platform-link:hover { opacity: 0.72; }
  .ab-platform-icon img {
    width: 224px;
    height: 50px;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ab-platform-icon--yt {  }
  .ab-platform-icon--sp { }
  .ab-platform-icon--ap {  }
  .ab-platform-name {
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    display: none;
  }

  /* ══════════════════════════════════════
     RESPONSIVE 768px
  ══════════════════════════════════════ */
  @media (max-width: 768px) {
    .ab-inner { padding-left: 32px; padding-right: 32px; }
    .ab-about__row { flex-direction: column; gap: 28px; }
    .ab-about { padding: 40px 0;}
    .ab-know {  padding: 0px 0 40px;}
    .ab-stats-col { flex-direction: row; flex-wrap: wrap; width: 100%; gap: 10px; }
    .ab-stat-card { flex: 1; min-width: 120px; }
    .ab-mic-col { width: 100%; }
    .ab-mic-img { height: 220px; width: 100%; }
    .ab-know__grid { grid-template-columns: 1fr; }
    .ab-flip-wrap { height: 280px; }
    .ab-testi__cards { grid-template-columns: 1fr; }
    .ab-testi__heading { font-size: 22px; margin-bottom:24px; }
    .ab-know__heading { font-size: 22px;margin-bottom: 24px; }
    .ab-listen {padding: 0px 0 40px;}
    .ab-listen__platforms { gap: 24px; }
    .ab-testi {    padding: 40px 0 40px;}
  }

  /* ══════════════════════════════════════
     RESPONSIVE 480px
  ══════════════════════════════════════ */
  @media (max-width: 480px) {
    .ab-inner { padding-left: 16px; padding-right: 16px; }
    .ab-about { padding: 25px 0 40px;}
    .ab-know__heading {font-size: 30px; margin-bottom: 30px; }
    .ab-flip-front { padding:24px 38px;}
    .ab-flip-front__text { font-size: 14px;}
    .ab-know__grid { gap:20px;}
    .ab-know {padding: 0px 0 60px;}
    .ab-listen { padding: 30px 0 54px;}
    .ab-listen__label {font-size: 20px;}
    .ab-listen__inner { gap:34px;}

    /* ── STAT CARDS: 2x2 grid, square, large numbers ── */
    .ab-stats-col {
      flex-direction: row;
      flex-wrap: wrap;
      width: 100%;
      gap: 27px;
    }
    .ab-about__row { gap:40px;}
    .ab-mic-img { height: 390px; width: 100%; }
    .ab-stat-card {
      flex: 0 0 calc(46% - 6px);
      width: calc(50% - 6px);
      aspect-ratio: 1 / 1;
      padding: 0;
      border-radius: 14px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
    .ab-stat-card__num {
      font-size: 38px;
      font-weight: 700;
      line-height: 1;
      letter-spacing: 1%;
    }
    .ab-stat-card__label {
      font-size: 16px;
      font-weight: 400;
      margin-top: 10px;
      letter-spacing: 0.2px;
    }

    .ab-brand-card { padding: 28px 20px; }
    .ab-listen__platforms { flex-direction: row; gap: 16px; }
    .ab-platform-icon img { width: 105px; height: 32px; gap:20px;}

    /* ── TESTIMONIALS: 1 card per slide on mobile ── */
    .ab-testi { padding: 60px 0 27px;}
    .ab-testi__label { font-size: 16px; }
    .ab-testi__heading { font-size: 30px; margin-bottom: 30px; }
    .ab-testi-slide--mobile {
      grid-template-columns: 1fr;
      gap: 0;
    }
    .ab-testi-card { padding: 28px 24px; }
    .ab-testi-card__quote { font-size: 16px; line-height: 155%; }
    .ab-testi-card__author { font-size: 16px; }
    .ab-testi-card__quote-icon img { width: 50px; height: 50px; }
  }
`;

/* ─── DATA ─────────────────────────────────── */

const STATS = [
  { num: "70+",  label: "Episodes",     delay: "ab-stat-card--d1" },
  { num: "20k+", label: "Downloads",    delay: "ab-stat-card--d2" },
  { num: "80+",  label: "Expert Guest", delay: "ab-stat-card--d3" },
  { num: "10+",  label: "Countries",    delay: "ab-stat-card--d4" },
];

const BRANDS = [
  {
    logo: LogoQuench,
    frontText: "We're not just another EV charging company. We're India's only true powerhouse that blends global charging intelligence with local grit. Every Quench Slimline is built like a warrior — engineered for speed, designed for endurance, and trusted by fleets and cities that demand nothing less than the best.",
    backText: "From highways to hidden corners of the country, Quench builds the backbone of India's EV revolution.",
    href: "https://quenchchargers.com/",
    alt: "Quench",
    variant: "",
  },
  {
    logo: LogoAdorDig,
    frontText: "Every great EV story begins at the cell. ADPL is where science, precision, and industrial muscle collide. From formation to validation, we don't just make batteries — we write the rulebook on consistency, safety, and trust.",
    backText: "We stand at every gate of the battery journey, ensuring one thing never changes: reliability.",
    href: "https://adordigatron.com/",
    alt: "Ador Digatron",
    variant: "",
  },
  {
    logo: LogoYonder,
    frontText: "Hydrogen isn't the future — it's already here. Yonder stands at the frontier, carrying India into the age of clean molecules. From mobility to heavy industry, we're not experimenting — we're executing.",
    backText: "Yonder is the bridge between ambition and action, where global hydrogen concepts turn into India's boldest energy realities.",
    href: "https://yonderh2.com/",
    alt: "Yonder",
    variant: "",
  },
  {
    logo: LogoAdor,
    frontText: "Not Just Values, Our Core Values",
    backText: "Hungry (hʌŋ.ɡri)Brutally Honest (broot.uhl.ee ɒn.ɪst)Innovative (in-uh-vey-tiv)Deserving (dɪˈzɜː.vɪŋ)",
    href: "https://adorpowertron.com/",
    alt: "Ador",
    variant: "ab-flip-wrap--ador",
  },
];

const TESTIMONIALS_PAGES = [
  [
    {
      quote: "Energizing India gives space to voices we don't usually hear. It's not just another industry show, it's a platform for ideas that actually matter",
      author: "Neha Sharma, Pune",
    },
    {
      quote: "As someone working in European clean tech, it's refreshing to hear such grounded, high-context takes from the Indian energy scene.",
      author: "Tom Alvarez, Berlin",
    },
  ],
  [
    {
      quote: "I've listened to a lot of energy podcasts, but this is the only one that actually makes me want to keep listening. The guests are sharp, the questions aren't sugarcoated, and it never feels like a sales pitch.",
      author: "Aparna Mehta, Mumbai",
    },
    {
      quote: "These episodes have become part of my Monday commute.",
      author: "Ravi Iyer, Bengaluru",
    },
  ],
];

/* ─── COUNT-UP ──────────────────────────────── */
function useCountUp(rawTarget, duration = 1600) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const num = parseInt(rawTarget.replace(/\D/g, ""), 10);
    const suffix = rawTarget.replace(/[0-9]/g, "");
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !fired.current) {
        fired.current = true;
        const steps = 40;
        const interval = duration / steps;
        let i = 0;
        const t = setInterval(() => {
          i++;
          const val = Math.round((i / steps) * num);
          setDisplay(val + suffix);
          if (i >= steps) clearInterval(t);
        }, interval);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [rawTarget, duration]);

  return { ref, display };
}

/* ─── SCROLL REVEAL ─────────────────────────── */
function useScrollReveal(selector, cls = "ab-in") {
  useEffect(() => {
    const els = document.querySelectorAll(selector);
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add(cls); }),
      { threshold: 0.12 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [selector, cls]);
}

/* ─── STAT SLIDE-IN ─────────────────────────── */
function useStatSlideIn() {
  useEffect(() => {
    const cards = document.querySelectorAll(".ab-stat-card");
    const t = setTimeout(() => {
      cards.forEach(c => c.classList.add("ab-slide-in"));
    }, 120);
    return () => clearTimeout(t);
  }, []);
}

/* ─── SVG ICONS ─────────────────────────────── */
function IconYT() {
  return <img src={IconYoutube} alt="YouTube" />;
}
function IconSP() {
  return <img src={IconSpotify} alt="Spotify" />;
}
function IconAP() {
  return <img src={IconApplePodcast} alt="Apple Podcasts" />;
}
function IconQuote() {
  return <img src={IconQuoteImg} alt="" className="ab-testi-card__quote-icon" width="30" height="30" />;
}

/* ─── STAT CARD ─────────────────────────────── */
function StatCard({ num, label, delay }) {
  const { ref, display } = useCountUp(num);
  return (
    <div ref={ref} className={`ab-stat-card ${delay}`}>
      <span className="ab-stat-card__num">{display}</span>
      <span className="ab-stat-card__label">{label}</span>
    </div>
  );
}

/* ─── FLIP CARD ─────────────────────────────── */
function FlipCard({ brand, index }) {
  return (
    <div className={`ab-flip-wrap ${brand.variant} ab-fade-up ab-delay-${(index % 2) + 1}`}>
      <div className="ab-flip-inner">
        {/* FRONT */}
        <div className="ab-flip-front">
          <img className="ab-flip-front__logo" src={brand.logo} alt={brand.alt} />
          <p className="ab-flip-front__text">{brand.frontText}</p>
        </div>
        {/* BACK */}
        <div className="ab-flip-back">
          <p className="ab-flip-back__text">{brand.backText}</p>
          <a href={brand.href} target="_blank" rel="noopener noreferrer" className="ab-flip-back__link">
            Click Here
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── ABOUT SECTION ─────────────────────────── */
function AboutSection() {
  useStatSlideIn();

  return (
    <section className="ab-about">
      <div className="ab-inner ab-about__row">

        {/* LEFT — stat cards */}
        <div className="ab-stats-col">
          {STATS.map((s, i) => (
            <StatCard key={i} num={s.num} label={s.label} delay={s.delay} />
          ))}
        </div>

        {/* CENTER — mic image */}
        <div className="ab-mic-col ab-fade-up">
          <img
            className="ab-mic-img"
            src={MicImg}
            alt="Energizing India Podcast microphone"
          />
        </div>

        {/* RIGHT — text */}
        <div className="ab-text-col ab-fade-up ab-delay-2">
          <h2 className="ab-text-col__heading">About Us</h2>
          <p>
            We have always been in an energy transition: wood fires gave way to the use of whale oil for heat
            and illumination, which then transitioned to hydrocarbon molecule-based energy sources (oil, petrol,
            diesel). Today the transition is moving towards zero emission mobility and power generation. The
            Energizing India Podcast was established to facilitate a greater understanding of the energy
            transition that the world is presently undergoing.
          </p>
          <p>
            We believe the best way to understand the future is to have real conversations with the people
            building it. Our podcast brings you sharp, unscripted dialogues with changemakers across the energy,
            mobility, and climate sectors. Inspired by the relaxed-yet-revealing interview genre of ABC
            Australia, we keep things honest and curious, yet always human. Guests featured on our show always
            hear their episode before it airs, because respect and clarity matter.
          </p>
          <p>
            But the podcast is just one part of our story. We're backed by a larger mission to facilitate the
            transition to zero emission energy and mobility by creating greater clarity of what needs to be done.
          </p>
          <p>
            If you have a story or technology on the energy transition sector which you believe the world would
            benefit from hearing, we would love to hear from you. We do not charge guests to be featured, but
            we do our best to make sure first that any story is worth featuring.
          </p>
        </div>

      </div>
    </section>
  );
}

/* ─── KNOW US BETTER ────────────────────────── */
function KnowUsSection() {
  useScrollReveal(".ab-fade-up");
  return (
    <section className="ab-know">
      <div className="ab-inner">
        <h2 className="ab-know__heading ab-fade-up">Know Us Better</h2>
        <div className="ab-know__grid">
          {BRANDS.map((b, i) => (
            <FlipCard key={i} brand={b} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── TESTIMONIALS ──────────────────────────── */

/* Flat list of all 4 testimonials for mobile 1-per-slide */
const TESTIMONIALS_FLAT = [
  { quote: "Energizing India gives space to voices we don't usually hear. It's not just another industry show, it's a platform for ideas that actually matter", author: "Neha Sharma, Pune" },
  { quote: "As someone working in European clean tech, it's refreshing to hear such grounded, high-context takes from the Indian energy scene.", author: "Tom Alvarez, Berlin" },
  { quote: "I've listened to a lot of energy podcasts, but this is the only one that actually makes me want to keep listening. The guests are sharp, the questions aren't sugarcoated, and it never feels like a sales pitch.", author: "Aparna Mehta, Mumbai" },
  { quote: "These episodes have become part of my Monday commute.", author: "Ravi Iyer, Bengaluru" },
];

function TestimonialsSection() {
  const [page, setPage]     = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 480);
  const trackRef            = useRef(null);
  const wrapRef             = useRef(null);
  const dragStart           = useRef(null);
  const dragCurrent         = useRef(0);
  const isDragging          = useRef(false);

  /* respond to resize */
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 480;
      setIsMobile(mobile);
      setPage(0);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* desktop uses TESTIMONIALS_PAGES (2-per-slide), mobile uses TESTIMONIALS_FLAT (1-per-slide) */
  const slides = isMobile
    ? TESTIMONIALS_FLAT.map(t => [t])
    : TESTIMONIALS_PAGES;
  const total = slides.length;

  const getOffset = (p) => `translateX(calc(-${p * 100}%))`;

  const onPointerDown = (e) => {
    isDragging.current  = false;
    dragStart.current   = e.clientX;
    dragCurrent.current = 0;
    wrapRef.current?.classList.add("ab-dragging");
    trackRef.current?.classList.add("ab-no-transition");
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup",   onPointerUp);
  };

  const onPointerMove = (e) => {
    if (dragStart.current === null) return;
    dragCurrent.current = e.clientX - dragStart.current;
    isDragging.current  = Math.abs(dragCurrent.current) > 5;
    if (isDragging.current && trackRef.current) {
      const baseOffset = page * trackRef.current.offsetWidth;
      trackRef.current.style.transform =
        `translateX(${-baseOffset + dragCurrent.current}px)`;
    }
  };

  const onPointerUp = () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup",   onPointerUp);
    wrapRef.current?.classList.remove("ab-dragging");
    trackRef.current?.classList.remove("ab-no-transition");

    if (isDragging.current) {
      const threshold = 60;
      if (dragCurrent.current < -threshold && page < total - 1) {
        setPage(p => p + 1);
      } else if (dragCurrent.current > threshold && page > 0) {
        setPage(p => p - 1);
      } else {
        if (trackRef.current) {
          trackRef.current.style.transform = getOffset(page);
        }
      }
    }

    dragStart.current   = null;
    dragCurrent.current = 0;
    isDragging.current  = false;
  };

  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.transform = getOffset(page);
    }
  }, [page]);

  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup",   onPointerUp);
    };
  }, []);

  return (
    <section className="ab-testi">
      <div className="ab-testi__bg" style={{ backgroundImage: `url(${TestimonialBg})` }} />
      <div className="ab-testi__overlay" />
      <div className="ab-inner ab-testi__content">
        <p className="ab-testi__label ab-fade-up">Testimonial</p>
        <h2 className="ab-testi__heading ab-fade-up ab-delay-1">What Our Listeners Are Saying</h2>

        <div
          ref={wrapRef}
          className="ab-testi__track-wrap"
          onPointerDown={onPointerDown}
        >
          <div ref={trackRef} className="ab-testi__track">
            {slides.map((slidecards, si) => (
              <div key={si} className={`ab-testi-slide${isMobile ? " ab-testi-slide--mobile" : ""}`}>
                {slidecards.map((t, i) => (
                  <div key={i} className={`ab-testi-card ab-fade-up ab-delay-${i + 1}`}>
                    <p className="ab-testi-card__quote">{t.quote}</p>
                    <div className="ab-testi-card__footer">
                      <span className="ab-testi-card__author">{t.author}</span>
                      <IconQuote />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="ab-testi__dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`ab-dot${i === page ? " ab-dot--on" : ""}`}
              onClick={() => setPage(i)}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── LISTEN & SUBSCRIBE ────────────────────── */
function ListenSection() {
  const platforms = [
    { href: "https://www.youtube.com/@EnergizingIndiaPodcast", name: "YouTube", cls: "ab-platform-icon--yt", icon: <IconYT /> },
    { href: "https://open.spotify.com/show/31CpjZm9DyfCR7sstBZlIf", name: "Spotify", cls: "ab-platform-icon--sp", icon: <IconSP /> },
    { href: "https://podcasts.apple.com/in/podcast/energizing-india-podcast/id1530917254", name: "Podcasts", cls: "ab-platform-icon--ap", icon: <IconAP /> },
  ];
  return (
    <section className="ab-listen">
      <div className="ab-inner ab-listen__inner">
        <span className="ab-listen__label">Listen &amp; Subscribe</span>
        <div className="ab-listen__platforms">
          {platforms.map((p, i) => (
            <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" className="ab-platform-link">
              <span className={`ab-platform-icon ${p.cls}`}>{p.icon}</span>
              <span className="ab-platform-name">{p.name}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── ROOT ──────────────────────────────────── */
export default function AboutPage() {
  return (
    <>
      <style>{CSS}</style>
      <div className="ab-wrap">
        <AboutSection />
        <KnowUsSection />
        <ListenSection />
        <TestimonialsSection />
      </div>
    </>
  );
}